'use client';
import Link, { LinkProps as NextLinkProps } from 'next/link';
import { FC, HTMLProps, useMemo } from 'react';
import { TypeCategorySkeleton, TypePageSkeleton, LinkPageType } from '@fm-nx/contentful-types';
import { getLinkToForPage, getUrlByPageType } from '@fm-nx/navigation';
import { PageContentTypes } from '@fm-nx/constants';

export interface LinkToPropsTypes {
    href: string;
    as?: string;
}

type LinkCustomPropTypes = {
    page?: TypePageSkeleton | TypeCategorySkeleton | LinkPageType | undefined;
    hrefProp?: string;
    slug?: string;
    pageType?: keyof typeof PageContentTypes;
};

// remove href from types so we can use our own href prop because href is required
// and this will build it based on if there is a page, path or pass through href
type LinkWrapperPropTypes = LinkCustomPropTypes & Omit<NextLinkProps, 'href'> & HTMLProps<HTMLAnchorElement>;

// when would you pass in a path and not an href?
// page: pageType mapped to url
// path: gets preview url
// href: is just a pass through
export const LinkWrapper: FC<LinkWrapperPropTypes> = (props) => {
    const { href: hrefProp = '#', page, children, ref, slug, pageType, ...rest } = props;

    const isExternal = useMemo(() => {
        if (typeof window === 'undefined') return false;
        if (!hrefProp || hrefProp.startsWith('/')) return false;

        const browserHostName = window.location.hostname;
        const splitName = hrefProp.split('/');

        if (splitName[2]) {
            const updatedName = splitName[2].split('.');
            const hostName = updatedName
                .filter((el: string) => {
                    return el !== 'www';
                })
                .shift();
            return hostName !== browserHostName;
        }
        return false;
    }, [hrefProp]);

    const getLinkToProps = (): LinkToPropsTypes => {
        // Check if page is from GraphQL (has __typename and slug properties directly)
        if (page && '__typename' in page && 'slug' in page && page.slug) {
            return getUrlByPageType({ pageType: page.__typename, slug: page.slug });
        }
        // Check if page is from Contentful SDK (has fields.slug)
        if (page && 'fields' in page && page.fields?.slug) {
            return getLinkToForPage({ page });
        }
        // new props - added for experiences, but should be used going forward
        if (pageType && slug) {
            return getUrlByPageType({
                pageType,
                slug,
            });
        }
        return { href: hrefProp };
    };

    const linkToProps = getLinkToProps();

    return (
        <Link {...rest} {...rest} {...linkToProps} target={isExternal ? '_blank' : '_self'}>
            {children}
        </Link>
    );
};

export default LinkWrapper;
